import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, MapPin, Percent, Clock, Plus, Pencil, Trash2, 
  ChevronLeft, Save, Loader2, ExternalLink, RotateCcw, AlertCircle, Target, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import AppHeader from '@/components/shared/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAX_TYPE_OPTIONS = [
  { value: 'GST', label: 'GST (Goods & Services Tax)' },
  { value: 'VAT', label: 'VAT (Value Added Tax)' },
  { value: 'Service Tax', label: 'Service Tax' },
  { value: 'CGST', label: 'CGST (Central GST)' },
  { value: 'SGST', label: 'SGST (State GST)' },
  { value: 'IGST', label: 'IGST (Integrated GST)' },
  { value: 'Other', label: 'Other' },
];

const THERAPY_CATEGORIES = [
  'Massage',
  'Facial',
  'Body Treatment',
  'Hair Care',
  'Nail Care',
  'Aromatherapy',
  'Ayurveda',
  'Wellness',
  'Other'
];

const ManageProperty = ({ user, onLogout }) => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [property, setProperty] = useState(null);
  const [therapies, setTherapies] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  
  // Property settings form
  const [settings, setSettings] = useState({
    google_maps_link: '',
    google_review_link: '',
    tax_types: ['GST'],
    tax_rate: 5.0,
    opening_time: '09:00',
    closing_time: '21:00',
    cleaning_buffer_minutes: 15,
    slot_duration_minutes: 30,
    // Target & Incentive Settings
    collective_target: 0,
    threshold_percent: 90,
    incentive_percentage: 10
  });
  
  // Therapy dialog
  const [therapyDialog, setTherapyDialog] = useState(false);
  const [editingTherapy, setEditingTherapy] = useState(null);
  const [therapyForm, setTherapyForm] = useState({
    name: '',
    duration: 60,
    base_price: 0,
    description: '',
    category: 'Massage'
  });
  const [therapyLoading, setTherapyLoading] = useState(false);
  
  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingTherapy, setDeletingTherapy] = useState(null);

  useEffect(() => {
    if (propertyId) {
      fetchPropertySettings();
      fetchTherapies();
    }
  }, [propertyId, showInactive]);

  const fetchPropertySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/property-management/settings/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Also fetch the full property for target settings
      const propResponse = await axios.get(`${API}/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const propData = propResponse.data;
      
      setProperty(response.data);
      setSettings({
        google_maps_link: response.data.google_maps_link || '',
        google_review_link: response.data.google_review_link || '',
        tax_types: response.data.tax_types || ['GST'],
        tax_rate: response.data.tax_rate || 5.0,
        opening_time: response.data.opening_time || '09:00',
        closing_time: response.data.closing_time || '21:00',
        cleaning_buffer_minutes: response.data.cleaning_buffer_minutes || 15,
        slot_duration_minutes: response.data.slot_duration_minutes || 30,
        // Target & Incentive Settings
        collective_target: propData.collective_target || 0,
        threshold_percent: propData.threshold_percent || 90,
        incentive_percentage: propData.incentive_percentage || 10
      });
    } catch (error) {
      console.error('Error fetching property settings:', error);
      toast.error('Failed to load property settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTherapies = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use hotel_name to fetch therapies
      const propResponse = await axios.get(`${API}/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const response = await axios.get(
        `${API}/property-management/therapies/${propResponse.data.hotel_name}?include_inactive=${showInactive}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTherapies(response.data);
    } catch (error) {
      console.error('Error fetching therapies:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Save property settings (tax, location, hours)
      await axios.put(`${API}/property-management/settings/${propertyId}`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Save target settings separately
      if (property?.hotel_name) {
        await axios.put(
          `${API}/therapist-performance/set-target/${encodeURIComponent(property.hotel_name)}`,
          {
            collective_target: settings.collective_target,
            threshold_percent: settings.threshold_percent,
            incentive_percentage: settings.incentive_percentage
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success('Settings saved successfully');
      fetchPropertySettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTaxTypeToggle = (taxType) => {
    setSettings(prev => {
      const current = prev.tax_types || [];
      if (current.includes(taxType)) {
        // Don't allow removing the last tax type
        if (current.length === 1) {
          toast.warning('At least one tax type is required');
          return prev;
        }
        return { ...prev, tax_types: current.filter(t => t !== taxType) };
      } else {
        return { ...prev, tax_types: [...current, taxType] };
      }
    });
  };

  const openAddTherapy = () => {
    setEditingTherapy(null);
    setTherapyForm({
      name: '',
      duration: 60,
      base_price: 0,
      description: '',
      category: 'Massage'
    });
    setTherapyDialog(true);
  };

  const openEditTherapy = (therapy) => {
    setEditingTherapy(therapy);
    setTherapyForm({
      name: therapy.name,
      duration: therapy.duration,
      base_price: therapy.base_price,
      description: therapy.description || '',
      category: therapy.category || 'Massage'
    });
    setTherapyDialog(true);
  };

  const saveTherapy = async () => {
    if (!therapyForm.name || !therapyForm.duration || !therapyForm.base_price) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setTherapyLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get hotel_name
      const propResponse = await axios.get(`${API}/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (editingTherapy) {
        // Update existing
        await axios.put(`${API}/property-management/therapies/${editingTherapy.id}`, therapyForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Therapy updated successfully');
      } else {
        // Create new
        await axios.post(`${API}/property-management/therapies`, {
          ...therapyForm,
          property_id: propResponse.data.hotel_name
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Therapy created successfully');
      }
      
      setTherapyDialog(false);
      fetchTherapies();
    } catch (error) {
      console.error('Error saving therapy:', error);
      toast.error(error.response?.data?.detail || 'Failed to save therapy');
    } finally {
      setTherapyLoading(false);
    }
  };

  const confirmDeleteTherapy = (therapy) => {
    setDeletingTherapy(therapy);
    setDeleteDialog(true);
  };

  const deleteTherapy = async () => {
    if (!deletingTherapy) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/property-management/therapies/${deletingTherapy.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Therapy deleted');
      setDeleteDialog(false);
      setDeletingTherapy(null);
      fetchTherapies();
    } catch (error) {
      console.error('Error deleting therapy:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete therapy');
    }
  };

  const restoreTherapy = async (therapyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/property-management/therapies/${therapyId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Therapy restored');
      fetchTherapies();
    } catch (error) {
      console.error('Error restoring therapy:', error);
      toast.error('Failed to restore therapy');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader user={user} onLogout={onLogout} showBack={true} backTo="/admin/properties" />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onLogout={onLogout} showBack={true} backTo="/admin/properties" />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/properties')}
                className="p-0 h-auto"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Properties
              </Button>
            </div>
            <h1 className="text-3xl font-serif text-foreground flex items-center gap-3">
              <Building2 className="w-8 h-8 text-primary" />
              {property?.hotel_name || 'Manage Property'}
            </h1>
            <p className="text-muted-foreground mt-1">{property?.location}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Location & Maps */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Location
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="google_maps_link">Google Maps Link</Label>
                  <Input
                    id="google_maps_link"
                    value={settings.google_maps_link}
                    onChange={(e) => setSettings(prev => ({ ...prev, google_maps_link: e.target.value }))}
                    placeholder="https://maps.google.com/..."
                    data-testid="google-maps-input"
                  />
                  {settings.google_maps_link && (
                    <a 
                      href={settings.google_maps_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary flex items-center gap-1 mt-2 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open in Maps
                    </a>
                  )}
                </div>
                <div>
                  <Label htmlFor="google_review_link">Google Review Link</Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    This link will be included in customer feedback emails to encourage Google reviews
                  </p>
                  <Input
                    id="google_review_link"
                    value={settings.google_review_link}
                    onChange={(e) => setSettings(prev => ({ ...prev, google_review_link: e.target.value }))}
                    placeholder="https://g.page/r/..."
                    data-testid="google-review-input"
                  />
                  {settings.google_review_link && (
                    <a 
                      href={settings.google_review_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 flex items-center gap-1 mt-2 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View Google Reviews
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tax Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-primary" />
                  Tax Configuration
                </CardTitle>
                <CardDescription>Configure applicable taxes for this property</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Tax Types (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {TAX_TYPE_OPTIONS.map(option => (
                      <label 
                        key={option.value}
                        className="flex items-center gap-2 p-2 rounded border hover:bg-muted/50 cursor-pointer"
                        data-testid={`tax-type-${option.value.toLowerCase()}`}
                      >
                        <Checkbox 
                          checked={settings.tax_types?.includes(option.value)}
                          onCheckedChange={() => handleTaxTypeToggle(option.value)}
                        />
                        <span className="text-sm">{option.value}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="tax_rate">Combined Tax Rate (%)</Label>
                  <Input
                    id="tax_rate"
                    type="number"
                    min="0"
                    max="50"
                    step="0.1"
                    value={settings.tax_rate}
                    onChange={(e) => setSettings(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                    data-testid="tax-rate-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Default: 5% (editable per property)
                  </p>
                </div>
                
                {/* Tax Preview */}
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-sm font-medium mb-2">Tax Preview</p>
                  <div className="text-xs space-y-1">
                    <p>Applied taxes: <span className="font-medium">{settings.tax_types?.join(' + ') || 'GST'}</span></p>
                    <p>Rate: <span className="font-medium">{settings.tax_rate}%</span></p>
                    <p className="text-muted-foreground">Example: ₹1000 base + ₹{(1000 * settings.tax_rate / 100).toFixed(2)} tax = ₹{(1000 + 1000 * settings.tax_rate / 100).toFixed(2)} total</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Spa Hours */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Operating Hours
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="opening_time">Opening Time</Label>
                    <Input
                      id="opening_time"
                      type="time"
                      value={settings.opening_time}
                      onChange={(e) => setSettings(prev => ({ ...prev, opening_time: e.target.value }))}
                      data-testid="opening-time-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="closing_time">Closing Time</Label>
                    <Input
                      id="closing_time"
                      type="time"
                      value={settings.closing_time}
                      onChange={(e) => setSettings(prev => ({ ...prev, closing_time: e.target.value }))}
                      data-testid="closing-time-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="cleaning_buffer">Cleaning Buffer (minutes)</Label>
                  <Input
                    id="cleaning_buffer"
                    type="number"
                    min="0"
                    max="60"
                    value={settings.cleaning_buffer_minutes}
                    onChange={(e) => setSettings(prev => ({ ...prev, cleaning_buffer_minutes: parseInt(e.target.value) || 0 }))}
                    data-testid="cleaning-buffer-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="slot_duration">Slot Duration (minutes)</Label>
                  <Select 
                    value={String(settings.slot_duration_minutes)} 
                    onValueChange={(v) => setSettings(prev => ({ ...prev, slot_duration_minutes: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="slot-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Target & Incentive Configuration */}
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-600" />
                  Monthly Target & Incentives
                </CardTitle>
                <CardDescription>Configure team targets and incentive rules</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="collective_target">Monthly Target (₹)</Label>
                  <Input
                    id="collective_target"
                    type="number"
                    min="0"
                    step="10000"
                    value={settings.collective_target}
                    onChange={(e) => setSettings(prev => ({ ...prev, collective_target: parseFloat(e.target.value) || 0 }))}
                    placeholder="e.g., 500000"
                    data-testid="collective-target-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Combined revenue target for all therapists
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="threshold_percent">Threshold (%)</Label>
                    <Input
                      id="threshold_percent"
                      type="number"
                      min="0"
                      max="100"
                      value={settings.threshold_percent}
                      onChange={(e) => setSettings(prev => ({ ...prev, threshold_percent: parseFloat(e.target.value) || 90 }))}
                      data-testid="threshold-percent-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Incentive kicks in above this %
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="incentive_percentage">Incentive (%)</Label>
                    <Input
                      id="incentive_percentage"
                      type="number"
                      min="0"
                      max="50"
                      value={settings.incentive_percentage}
                      onChange={(e) => setSettings(prev => ({ ...prev, incentive_percentage: parseFloat(e.target.value) || 10 }))}
                      data-testid="incentive-percent-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      % of amount above threshold
                    </p>
                  </div>
                </div>
                
                {/* Incentive Preview */}
                {settings.collective_target > 0 && (
                  <div className="p-3 bg-amber-100/50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-amber-600" />
                      <p className="text-sm font-medium text-amber-800">Incentive Preview</p>
                    </div>
                    <div className="text-xs space-y-1 text-amber-700">
                      <p>Target: ₹{settings.collective_target.toLocaleString()}</p>
                      <p>{settings.threshold_percent}% threshold: ₹{(settings.collective_target * settings.threshold_percent / 100).toLocaleString()}</p>
                      <p className="font-medium">
                        Sales above threshold earn {settings.incentive_percentage}% incentive, split equally among all therapists
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button 
              onClick={saveSettings} 
              disabled={saving}
              className="w-full"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Settings</>
              )}
            </Button>
          </div>

          {/* Therapies Panel */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Therapies & Services</CardTitle>
                    <CardDescription>
                      Manage the services offered at this property ({therapies.filter(t => t.status === 'active').length} active)
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="showInactive" 
                        checked={showInactive}
                        onCheckedChange={setShowInactive}
                      />
                      <Label htmlFor="showInactive" className="text-sm text-muted-foreground cursor-pointer">
                        Show inactive
                      </Label>
                    </div>
                    <Button onClick={openAddTherapy} data-testid="add-therapy-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Therapy
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {therapies.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No therapies configured for this property</p>
                    <p className="text-sm">Add therapies to enable booking and service recording</p>
                    <Button onClick={openAddTherapy} variant="outline" className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Therapy
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Duration</TableHead>
                        <TableHead className="text-right">Base Price</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {therapies.map(therapy => (
                        <TableRow key={therapy.id} className={therapy.status === 'inactive' ? 'opacity-50' : ''}>
                          <TableCell className="font-medium">{therapy.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{therapy.category || 'General'}</Badge>
                          </TableCell>
                          <TableCell className="text-right">{therapy.duration} min</TableCell>
                          <TableCell className="text-right">₹{therapy.base_price.toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge variant={therapy.status === 'active' ? 'default' : 'secondary'}>
                              {therapy.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {therapy.status === 'inactive' ? (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => restoreTherapy(therapy.id)}
                                  title="Restore"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              ) : (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => openEditTherapy(therapy)}
                                    data-testid={`edit-therapy-${therapy.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => confirmDeleteTherapy(therapy)}
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`delete-therapy-${therapy.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add/Edit Therapy Dialog */}
      <Dialog open={therapyDialog} onOpenChange={setTherapyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTherapy ? 'Edit Therapy' : 'Add New Therapy'}</DialogTitle>
            <DialogDescription>
              {editingTherapy ? 'Update therapy details' : 'Add a new therapy/service for this property'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="therapy_name">Therapy Name *</Label>
              <Input
                id="therapy_name"
                value={therapyForm.name}
                onChange={(e) => setTherapyForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Swedish Massage"
                data-testid="therapy-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="therapy_duration">Duration (minutes) *</Label>
                <Input
                  id="therapy_duration"
                  type="number"
                  min="15"
                  step="15"
                  value={therapyForm.duration}
                  onChange={(e) => setTherapyForm(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                  data-testid="therapy-duration-input"
                />
              </div>
              
              <div>
                <Label htmlFor="therapy_price">Base Price (₹) *</Label>
                <Input
                  id="therapy_price"
                  type="number"
                  min="0"
                  value={therapyForm.base_price}
                  onChange={(e) => setTherapyForm(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                  data-testid="therapy-price-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="therapy_category">Category</Label>
              <Select 
                value={therapyForm.category} 
                onValueChange={(v) => setTherapyForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger data-testid="therapy-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {THERAPY_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="therapy_description">Description</Label>
              <Textarea
                id="therapy_description"
                value={therapyForm.description}
                onChange={(e) => setTherapyForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the therapy..."
                rows={3}
                data-testid="therapy-description-input"
              />
            </div>
            
            {/* Price Preview */}
            {therapyForm.base_price > 0 && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm font-medium">Price Preview</p>
                <div className="text-xs mt-1 space-y-1">
                  <p>Base: ₹{therapyForm.base_price.toLocaleString()}</p>
                  <p>Tax ({settings.tax_rate}%): ₹{(therapyForm.base_price * settings.tax_rate / 100).toFixed(2)}</p>
                  <p className="font-medium">Total: ₹{(therapyForm.base_price + therapyForm.base_price * settings.tax_rate / 100).toFixed(2)}</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTherapyDialog(false)}>Cancel</Button>
            <Button onClick={saveTherapy} disabled={therapyLoading} data-testid="save-therapy-btn">
              {therapyLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <>{editingTherapy ? 'Update' : 'Add'} Therapy</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Therapy</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingTherapy?.name}"? This therapy will be marked as inactive and can be restored later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteTherapy}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageProperty;
